import type { FetchResult } from '@apollo/client';
import { useSessionStorage } from 'react-use';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { AllergyRecord } from '@models/allergy';
import { AppointmentRecord } from '@models/appointment';
import type { SharedAssessmentResult } from '@models/assessment/shared';
import { DiagnosticReportRecord } from '@models/diagnostic-report';
import { EncounterRecord } from '@models/encounter';
import { FamilyHistoryRecord } from '@models/family-history';
import { GeneticInformationObservationRecord } from '@models/genetic-information';
import { ImmunizationRecord } from '@models/immunization';
import { InsurancePlan } from '@models/insurance';
import { LabResultObservationRecord } from '@models/lab-result';
import { LegalDirectiveRecord } from '@models/legal-directive';
import { MedicalConditionRecord } from '@models/medical-condition';
import { MedicationStatementRecord } from '@models/medication';
import { ObservationRecord } from '@models/observation';
import { PatientRecord } from '@models/patient';
import { ProcedureRecord } from '@models/procedure';
import { RelatedPersonRecord } from '@models/related-person';
import type { SharedScanResult } from '@models/scan';
import { VitalSignObservationRecord } from '@models/vital-sign';

import type {
  CreateDhaShareLinkMutation,
  CreateDhaShareLinkMutationVariables,
  CreateShareLinkMutation,
  CreateShareLinkMutationVariables,
  RemoveSharedResourceAccessMutationVariables,
  RemoveSharedResourceAccessMutation,
} from 'generated/shared_resource.generated';
import {
  ListSharedResourceHistoryDocument,
  useCreateDhaShareLinkMutation,
  useCreateShareLinkMutation,
  useGetSharedResourceQuery,
  useListSharedResourceHistoryQuery,
  useRemoveSharedResourceAccessMutation,
} from 'generated/shared_resource.generated';
import type {
  AllergyModel,
  Annotation,
  AppointmentModel,
  CategoryCode,
  CodeableConcept,
  CodeableConceptClinicalStatus,
  ConditionModel,
  CoverageModel,
  DiagnosticReportModel,
  EncounterModel,
  EncounterParticipant,
  FamilyMemberHistoryModel,
  HumanName,
  ImmunizationModel,
  ConsentModel,
  LocationModel,
  MedicationStatementModel,
  ObservationModel,
  PatientModel,
  ProcedureModel,
  Reference,
  RelatedPersonModel,
  SharedScanResultModel,
  SharingAssociationUnion,
} from 'generated/types';

export const useGetShareHistory = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const user_id = activeProfile?.user_id ?? '';
  const response = useListSharedResourceHistoryQuery({
    variables: {
      input: { user_id },
    },
    skip: !user_id,
  });
  handleError(response.error);
  return {
    ...response,
    data: response.data?.listSharedResourceHistory,
  };
};

export const useGetSharedResource = (share_link: string, cursor?: string) => {
  const [token] = useSessionStorage('share_token');

  const response = useGetSharedResourceQuery({
    variables: {
      input: {
        share_link,
        cursor,
      },
    },
    skip: !share_link,
    fetchPolicy: 'no-cache',
    context: {
      headers: {
        authorization: token,
      },
    },
  });
  const { data } = response;

  const { resource } = data?.getSharedResource ?? {};
  const transformedResource = transformResource(resource);

  return {
    ...response,
    data: {
      ...data?.getSharedResource,
      resource: transformedResource,
    },
  };
};

export const transformResource = (
  resources?: Partial<
    SharingAssociationUnion & {
      // Needed bc AppointmentModel's participant field is of type [Participant!]!"
      // and EncounterModel's participant field is of type "[EncounterParticipant!]
      encounterParticipant?: EncounterParticipant[] | null;
      // note: FamilyHistoryModel is [Annotation!] VS ProcedureModel is [Annotation]
      familyHistoryNote?: Annotation[] | null;
      // clinicalStatus: Allergy's type is CodeableConceptClinicalStatus! VS Condition's type is CodeableConceptConditionClinicalStatus
      allergyStatus?: CodeableConceptClinicalStatus | null;
      // category: AllergyModel is [CategoryCode!] VS ConditionModel is [CodeableConcept!]
      allergyCategory?: CategoryCode[] | null;
      // code: AllergyModel is CodeableConcept VS ConditionModel and Procedure is CodeableConcept!
      allergyCode?: CodeableConcept | null;
      // category: MedicationStatement is CodeableConcept VS Condition is [CodeableConcept!]
      medicationCategory?: CodeableConcept | null;
      // locationResource: Immunizations are LocationModel VS Others are [LocationModel!]
      immunizationLocationResource?: LocationModel | null;
      // Observation
      obsCategory?: CodeableConcept[] | null;
      obsSubject?: Reference | null;
      // Legal directives
      legalDirectivesCategory?: CodeableConcept[] | null;
      // Related person
      relatedPersonRelation?: CodeableConcept[] | null;
      // Patient
      patientName?: HumanName[] | null;
      obsPerformer?: (Reference | null)[] | null;
      obsCode?: CodeableConcept;
      // Diagnostic report
      diagnosticSubject?: Reference | null;
      diagnosticPerformer?: (Reference | null)[] | null;
    }
  >[]
) => {
  return resources?.map((resource) => {
    switch (resource.resourceType) {
      case 'AllergyIntolerance':
        return new AllergyRecord({
          ...resource,
          category: resource.allergyCategory,
          clinicalStatus: resource.allergyStatus,
          code: resource.allergyCode,
        } as AllergyModel);
      case 'Appointment':
        return new AppointmentRecord(resource as AppointmentModel);
      case 'Condition':
        return new MedicalConditionRecord(resource as ConditionModel);
      case 'Consent':
        return new LegalDirectiveRecord({
          ...resource,
          category: resource.legalDirectivesCategory,
        } as ConsentModel);
      case 'Coverage':
        return new InsurancePlan(resource as CoverageModel);
      case 'FamilyMemberHistory':
        return new FamilyHistoryRecord({
          ...resource,
          note: resource.familyHistoryNote,
        } as FamilyMemberHistoryModel);
      case 'DiagnosticReport':
        return new DiagnosticReportRecord({
          ...resource,
          subject: resource.diagnosticSubject,
          performer: resource.diagnosticPerformer,
        } as DiagnosticReportModel);
      case 'Encounter':
        return new EncounterRecord({
          ...resource,
          participant: resource.encounterParticipant,
        } as EncounterModel);
      case 'Immunization':
        return new ImmunizationRecord({
          ...resource,
          locationResource: resource.immunizationLocationResource,
        } as ImmunizationModel);
      case 'MedicationStatement':
        return new MedicationStatementRecord({
          ...resource,
          category: resource.medicationCategory,
        } as MedicationStatementModel);
      case 'Patient':
        return new PatientRecord({
          ...resource,
          name: resource.patientName,
        } as PatientModel);
      case 'Procedure':
        return new ProcedureRecord(resource as ProcedureModel);
      case 'RelatedPerson':
        return new RelatedPersonRecord({
          ...resource,
          relationship: resource.relatedPersonRelation,
        } as RelatedPersonModel);
      case 'Observation':
        // TODO: Will have new way of fetching a particular category of obs
        // eslint-disable-next-line no-case-declarations
        const categories = resource.obsCategory
          ?.map((a) => a.coding?.map(({ code }) => code))
          .join(',');
        if (categories?.includes('genetic-information')) {
          return new GeneticInformationObservationRecord({
            ...resource,
            category: resource.obsCategory,
            subject: resource.obsSubject,
          } as ObservationModel);
        } else if (categories?.includes('laboratory')) {
          return new LabResultObservationRecord({
            ...resource,
            category: resource.obsCategory,
            subject: resource.obsSubject,
          } as ObservationModel);
        } else if (
          categories?.includes('vital-signs') ||
          categories?.includes('sleep') ||
          categories?.includes('fitness') ||
          categories?.includes('body')
        ) {
          // Cast like the sibling branches: `resource` is the whole shared
          // resource union, so fields that only exist on other resource types
          // (e.g. MedicationStatement's narrowed `contained`) ride along in the
          // spread and don't fit ObservationModel.
          return new VitalSignObservationRecord({
            ...resource,
            performer: resource.obsPerformer,
            status: 'final',
            category: resource.obsCategory,
            code: resource.obsCode ?? {},
            bodySite: null,
            specimen: null,
            partOf: null,
            focus: null,
            __typename: 'ObservationModel',
          } as ObservationModel);
        } else {
          return null;
        }
      case 'SharedAssessmentResult':
        // Already shaped for the result view by the backend; the mapping into
        // AssessmentResultData happens in @models/assessment/result.
        return resource as SharedAssessmentResult;
      case 'SharedScanResult':
        // eslint-disable-next-line no-case-declarations
        const record = resource as SharedScanResultModel;
        return {
          ...record,
          observations:
            record.observations?.map((obs) => new ObservationRecord(obs)) ?? [],
        } as SharedScanResult;
      default:
        return null;
    }
  });
};

export function useCreateShareLink(): [
  (
    params: CreateShareLinkMutationVariables
  ) => Promise<FetchResult<CreateShareLinkMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'createShareLink',
  });
  const [mutate, { loading: baseLoading }] = useCreateShareLinkMutation();
  const loading = baseLoading;

  async function createShareLink(params: CreateShareLinkMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.createShareLink && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [createShareLink, loading];
}

export function useCreateDhaShareLink(): [
  (
    params: CreateDhaShareLinkMutationVariables
  ) => Promise<FetchResult<CreateDhaShareLinkMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'createShareLink',
  });
  const [mutate, { loading }] = useCreateDhaShareLinkMutation();

  async function createDhaShareLink(
    params: CreateDhaShareLinkMutationVariables
  ) {
    const response = await mutate({ variables: { ...params } });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.createDhaShareLink && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [createDhaShareLink, loading];
}

export function useRevokeSharedLinkAccess(): [
  (
    params: RemoveSharedResourceAccessMutationVariables
  ) => Promise<FetchResult<RemoveSharedResourceAccessMutation>>,
  boolean,
] {
  const {
    state: { activeProfile },
  } = useSession();
  const user_id = activeProfile?.user_id ?? '';
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'revokeShareLinkAccess',
  });

  const [mutate, { loading: baseLoading }] =
    useRemoveSharedResourceAccessMutation();
  const loading = baseLoading;

  async function revokeAccess(
    params: RemoveSharedResourceAccessMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      refetchQueries: [
        {
          query: ListSharedResourceHistoryDocument,
          variables: {
            input: {
              user_id,
            },
          },
        },
      ],
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.removeSharedResourceAccess && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [revokeAccess, loading];
}
